import React, {
  useEffect,
  useState,
  useCallback,
} from 'react'
import { useHistory, useParams, generatePath } from 'react-router-dom'
import {
  requestOrderInfo,
  requestOrderCancel,
} from 'requests/sales'
import {
  requestAddCartItem,
  requestRemoveCartItem,
  requestUpdateCartItem,
  requestConfirmOutOfStock,
} from 'requests/order-item'
import {
  requestConfirmOrder,
  requestCancelPayment,
  requestReopenOrder,
} from 'requests/checkout'
import { requestGeneralDiscount } from 'requests/cart'
import useCheckoutPayment from 'hooks/useCheckoutPayment'
import { simpleStrDate, currencyFormat } from 'utils/format'
import { SALES_SUBMENU } from 'constants/menu/submenus'
import { EMPTY_ORDER_ITEM } from 'constants/objects/order-item'
import {
  ORDER_STATUS_OPEN,
  ORDER_STATUS_FINISHED,
  ORDER_STATUS_CANCELED,
} from 'constants/order-status'
import { CHECKOUT_CUSTOMER_PENDINGS, CHECKOUT_PAYMENT } from 'constants/app-paths'

import { Skeleton } from 'ui-components/Skeleton'
import { ApiErrors } from 'ui-components/ApiErrors'
import { SideMenu } from 'components/SideMenu'
import { InfoPage } from 'templates/InfoPage'
import { Card } from 'ui-components/Card'
import { Confirm } from 'ui-components/Confirm'
import { Button } from 'ui-components/Button'
import { Alert } from 'ui-components/Alert'

import { Item } from './components/Item'
import { AddItemForm } from './components/AddItemForm'
import { EditItemForm } from './components/EditItemForm'
import { PaymentInfo } from './components/PaymentInfo'
import { GeneralDiscount } from './components/GeneralDiscount'
import { TotalValuesDesktop, TotalValuesMobile, ButtonContainer } from './styles'

export default function Order() {
  const [order, setOrder] = useState({})
  const [loading, setLoading] = useState(false)
  const [responseError, setResponseError] = useState()
  const [toShowDeleteConfirm, setToShowDeleteConfirm] = useState(false)
  const [toShowEditForm, setToShowEditForm] = useState(false)
  const [currentItem, setCurrentItem] = useState()
  const [saving, setSaving] = useState(false)
  const [toShowCancelConfirm, setToShowCancelConfirm] = useState(false)
  const [toShowDiscountForm, setToShowDiscountForm] = useState(false)

  const { id } = useParams()
  const history = useHistory()
  const [, { initializePayment }] = useCheckoutPayment()

  const getOrder = useCallback(async () => {
    setLoading(true)
    const response = await requestOrderInfo(id)

    setLoading(false)
    if (response.error) return setResponseError(response)

    return setOrder(response.data)
  }, [id])

  useEffect(() => {
    getOrder()
  }, [getOrder])

  const onAddProduct = async ({ quantity, product }) => {
    setLoading(true)
    const item = {
      ...EMPTY_ORDER_ITEM,
      quantity,
      salable_id: product.id,
      value: product.attributes.selling_price,
      order_id: order.id,
    }

    const response = await requestAddCartItem(order.id, item)
    setLoading(false)

    if (response.error) return setResponseError(response)
    return getOrder()
  }

  const onEdit = (item) => {
    setCurrentItem(item)
    setToShowEditForm(true)
  }

  const onSave = async (item) => {
    setSaving(true)

    const params = { order_item: item }
    const response = await requestUpdateCartItem({
      orderId: id,
      itemId: currentItem.id,
      params,
    })

    setSaving(false)
    setToShowEditForm(false)
    if (response.error) return setResponseError(response)
    return getOrder()
  }

  const onSaveDiscount = async (value) => {
    setSaving(true)
    const params = {
      order_id: id,
      order_item_ids: order.attributes.items.map((item) => item.id),
      discount_percentage: value,
    }

    const response = await requestGeneralDiscount(params)
    setSaving(false)
    setToShowDiscountForm(false)
    if (response.error) return setResponseError(response)

    return setOrder(response.data)
  }

  const onDelete = (item) => {
    setCurrentItem(item)
    setToShowDeleteConfirm(true)
  }

  const onDeleteItem = async () => {
    setSaving(true)
    const response = await requestRemoveCartItem({
      orderId: id,
      itemId: currentItem.id,
    })

    setSaving(false)
    setToShowDeleteConfirm(false)
    if (response.error) return setResponseError(response)

    return getOrder()
  }

  const onConfirmOutOfStock = async (itemId) => {
    setSaving(true)

    const response = await requestConfirmOutOfStock({
      orderId: id,
      itemId,
    })

    setSaving(false)
    if (response.error) return setResponseError(response)

    return getOrder()
  }

  const onConfirmOrder = async () => {
    setSaving(true)
    const response = await requestConfirmOrder(order.id)

    setSaving(false)
    if (response.error) return setResponseError(response)

    return setOrder(response.data)
  }

  const onReopenOrder = async () => {
    setSaving(true)
    const response = await requestReopenOrder(order.id)
    setSaving(false)
    if (response.error) return setResponseError(response)

    return setOrder(response.data)
  }

  const onCancelOrder = async () => {
    setSaving(true)
    const response = await requestOrderCancel(order.id)
    setSaving(false)
    if (response.error) return setResponseError(response)

    setToShowCancelConfirm(false)
    return setOrder(response.data)
  }

  const onRegisterPayment = async () => {
    const { customer_id: customerId, customer_info: info } = order.attributes
    await initializePayment({
      customerId,
      item: order,
      customerName: info.name,
    })

    if (!customerId) return history.push(CHECKOUT_PAYMENT)

    const path = generatePath(CHECKOUT_CUSTOMER_PENDINGS, { id: customerId })
    return history.push(path)
  }

  const onCancelPayment = async () => {
    const response = await requestCancelPayment(order.attributes.payment.id)

    if (response.error) return setResponseError(response)

    return getOrder()
  }

  const renderErrorMessage = () => (
    <ApiErrors responseError={responseError} />
  )

  const renderLoading = () => (
    <Skeleton />
  )

  const renderMenu = () => (
    <SideMenu submenu={SALES_SUBMENU} />
  )

  const renderItem = (item) => (
    <Item
      item={item}
      key={item.id}
      onEdit={onEdit}
      onDelete={onDelete}
      orderId={order.id}
      onConfirmOutOfStock={onConfirmOutOfStock}
      updating={saving}
      preview={order.attributes.was_paid || order.attributes.order_status !== ORDER_STATUS_OPEN}
    />
  )

  const renderFinishButton = () => {
    if (order.attributes.order_status !== ORDER_STATUS_OPEN) return null

    return (
      <ButtonContainer>
        <Button onClick={() => setToShowCancelConfirm(true)} color="secondary">
          Cancelar Pedido
        </Button>

        <Button
          disabled={order.attributes.items.length === 0}
          onClick={() => setToShowDiscountForm(true)}
        >
          Desconto %
        </Button>

        <Button onClick={onConfirmOrder} color="primary">
          Fechar Pedido
        </Button>
      </ButtonContainer>
    )
  }

  const renderReopenOrder = () => {
    const { order_status: status, was_paid: paid } = order.attributes
    if (status !== ORDER_STATUS_FINISHED || paid) return null

    return (
      <ButtonContainer>
        <Button onClick={onReopenOrder}>
          Reabrir Pedido
        </Button>
      </ButtonContainer>
    )
  }

  const renderOrderStatus = () => {
    if (order.attributes.was_paid) {
      return (
        <div>
          <br />
          <PaymentInfo
            payment={order.attributes.payment}
            onCancel={onCancelPayment}
          />
        </div>
      )
    }

    if (order.attributes.order_status === ORDER_STATUS_FINISHED) {
      return (
        <div>
          <br />
          <Alert severity="success">
            Pedido finalizado
          </Alert>
        </div>
      )
    }

    return null
  }

  const renderCancelInfo = () => {
    if (order.attributes.order_status !== ORDER_STATUS_CANCELED) return null

    return (
      <Alert severity="error">
        {`Pedido cancelado em ${simpleStrDate(order.attributes.canceled_at)}`}
      </Alert>
    )
  }

  const renderBottom = () => {
    if (order.attributes.order_status === ORDER_STATUS_OPEN) {
      return (
        <AddItemForm
          onAddProduct={onAddProduct}
        />
      )
    }

    if (order.attributes.was_paid) return null

    if (order.attributes.order_status === ORDER_STATUS_FINISHED) {
      return (
        <ButtonContainer>
          <Button onClick={onRegisterPayment} color="primary">
            Pagamento
          </Button>
        </ButtonContainer>
      )
    }

    return null
  }

  return order?.id ? (
    <InfoPage
      title={`Pedido nº ${order.attributes.code} - ${order.attributes.status}`}
      subtitle={`${simpleStrDate(order.attributes.date)} - ${order.attributes.channel}`}
      renderMenu={renderMenu}
      onEdit={() => {}}
    >
      <Confirm
        open={toShowDeleteConfirm}
        title="Remover item"
        onCancel={() => setToShowDeleteConfirm(false)}
        onConfirm={onDeleteItem}
        cancelButtonText="Cancelar"
        confirmButtonText="Sim, remover item"
        requesting={saving}
        type="warning"
      >
        <p>Deseja realmente remover este item?</p>
      </Confirm>

      <Confirm
        open={toShowCancelConfirm}
        title="Cancelar pedido"
        onCancel={() => setToShowCancelConfirm(false)}
        onConfirm={onCancelOrder}
        cancelButtonText="Fechar"
        confirmButtonText="Sim, cancelar pedido"
        requesting={saving}
        type="warning"
      >
        <p>
          Deseja realmente cancelar este pedido?
          <br />
          Esta ação não poderá ser desfeita.
        </p>
      </Confirm>

      <GeneralDiscount
        open={toShowDiscountForm}
        onCancel={() => setToShowDiscountForm(false)}
        onSave={onSaveDiscount}
        saving={saving}
      />

      <EditItemForm
        item={currentItem}
        open={toShowEditForm}
        onCancel={() => setToShowEditForm(false)}
        onSave={onSave}
        saving={saving}
      />

      <Card>
        { loading ? renderLoading() : null }

        { renderCancelInfo() }

        <div>
          <strong>
            { `Cliente: ${order.attributes.customer_info.name}` }
          </strong>
        </div>

        { responseError ? renderErrorMessage() : null }

        <h4>Produtos</h4>

        {order.attributes.items.map(renderItem)}

        <TotalValuesDesktop>
          <div>TOTAL</div>
          <div>
            {currencyFormat(order.attributes.gross_value, 'R$')}
          </div>
          <div>
            {currencyFormat(order.attributes.total_discount, 'R$')}
          </div>
          <div>
            {currencyFormat(order.attributes.net_value, 'R$')}
          </div>
        </TotalValuesDesktop>

        <TotalValuesMobile>
          <div className="total">
            <div>SUBTOTAL</div>
            {currencyFormat(order.attributes.gross_value, 'R$')}
          </div>
          <div className="total">
            <div>DESCONTO</div>
            {currencyFormat(order.attributes.total_discount, 'R$')}
          </div>
          <div className="total">
            <div>TOTAL</div>
            {currencyFormat(order.attributes.net_value, 'R$')}
          </div>
        </TotalValuesMobile>

        { renderFinishButton() }
        { renderReopenOrder() }
      </Card>

      { renderOrderStatus() }

      { renderBottom() }

    </InfoPage>
  ) : null
}
